import dayjs from "dayjs";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFont } from "@similarweb/styles";
import { PopupHoverContainer } from "@similarweb/ui-components";
import type { IPopupHoverContainerProps } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { noDataFilter, numberFilter } from "app/filters/ngFilters";
import DurationService from "app/services/DurationService";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { ITableCellProps } from "../interfaces/ITableCellProps";

import { RightAlignCell } from "./WaKeywordPosition";

const popupConfig = {
    placement: "top",
};

export const PopupHoverContainerStyled = styled<React.ComponentType<IPopupHoverContainerProps>>(PopupHoverContainer)`
    .Popup-content {
        padding: 8px 16px;
    }
`;
const Text = styled.span`
    ${setFont({ $color: colorsPalettes.carbon[500], $size: 14, $weight: 400 })};
`;

const UpdateText = styled.div`
    ${setFont({ $color: colorsPalettes.carbon[300], $size: 12, $weight: 400 })};
`;
const SWReactIconsStyled = styled(SWReactIcons)`
    padding-top: 2px;
`;
const TextColor = styled.div<{ NumberColor: string }>`
    color: ${({ NumberColor }) => NumberColor};
    font-size: 14px;
    font-width: 400;
`;

const ChangeAbs = ({ value, decimal = 0 }: { value: number; decimal?: number }) => {
    const iconName = (value > 0 ? "arrow-up" : value < 0 ? "arrow-down" : "") as IconName;
    const NumberColor = value > 0 ? colorsPalettes.green.s100 : colorsPalettes.red.s100;
    return (
        <div
            className="text-right changePercentage"
            style={{ display: "flex" }}
        >
            <SWReactIconsStyled
                iconName={iconName}
                size={"xs"}
            />
            &nbsp;
            <TextColor NumberColor={NumberColor}>{numberFilter()(Math.abs(value), decimal)}</TextColor>
        </div>
    );
};

const getPreviousDate = () => {
    const swNavigator = proNavigator;
    const { duration } = swNavigator.getParams();
    const { to } = DurationService.getDurationData(duration).raw;
    return to.subtract(1, "month").format("MMM YYYY");
};

const i18n = i18nFilter();

const KeywordAnalysisPositionChangeTooltipContent = ({
    change,
    value,
    previousPosition,
    LatestUpdate,
    decimal,
}: FixmeType) => {
    const previousDate = getPreviousDate();
    const isNew = previousPosition === 0 && value > 0;
    const isLost = value === 0 && previousPosition > 0;
    const arePositionsUnavailable = value === 0 && previousPosition === 0;
    const arePositionsAvailable = previousPosition > 0 && value > 0 && change;

    const getTooltipContentByState = () => {
        if (arePositionsAvailable) {
            return (
                <>
                    <FlexRow>
                        <Text>
                            {i18n("position.filter.tooltip.part1", {
                                currentPosition: numberFilter()(value, decimal),
                            })}
                        </Text>
                        <ChangeAbs
                            value={change}
                            decimal={decimal}
                        />
                        &nbsp;
                        <Text>
                            {i18n("position.filter.tooltip.part2", {
                                previousPosition: numberFilter()(previousPosition, decimal),
                                date: previousDate,
                            })}
                        </Text>
                    </FlexRow>
                    <UpdateText>
                        {i18n("position.filter.tooltip.last.updated")}&nbsp;
                        {dayjs(LatestUpdate).format("MMM DD, YYYY")}
                    </UpdateText>
                </>
            );
        } else if (isNew) {
            return (
                <FlexRow>
                    <Text>
                        {i18n("position.filter.tooltip.new.part.1", {
                            currentPosition: numberFilter()(value, decimal),
                        })}
                    </Text>
                    <TextColor NumberColor={`${colorsPalettes.green.s100}`}>
                        {i18n("position.filter.tooltip.new")}
                    </TextColor>
                    &nbsp;
                    <Text>{i18n("position.filter.tooltip.new.part.2")}</Text>
                </FlexRow>
            );
        } else if (isLost) {
            return (
                <Text>
                    {i18n("position.filter.tooltip.lost", {
                        previousPosition: numberFilter()(previousPosition, decimal),
                        date: previousDate,
                    })}
                </Text>
            );
        } else if (arePositionsUnavailable) {
            return <Text>{i18n("position.filter.tooltip.no.pos.available")}</Text>;
        } else {
            return (
                <Text>
                    {i18n("position.filter.tooltip.no.change", {
                        currentPosition: numberFilter()(value, decimal),
                        latMonthInShortMonthAndYear: previousDate,
                    })}
                </Text>
            );
        }
    };
    return getTooltipContentByState();
};

export const KeywordAnalysisPosition = ({
    value,
    row,
    showTooltip,
    decimal = 0,
}: ITableCellProps & { showTooltip: boolean; decimal: number }) => {
    const previousPosition = row.PreviousPosition;
    const currentPosition = row.CurrentPosition;
    const change = previousPosition - currentPosition;
    if (value === "NaN") {
        return null;
    }
    if (showTooltip) {
        return (
            <PopupHoverContainerStyled
                config={popupConfig}
                content={() => (
                    <KeywordAnalysisPositionChangeTooltipContent
                        change={change}
                        value={value}
                        previousPosition={previousPosition}
                        LatestUpdate={row.LatestKeywordSerpDate}
                        decimal={decimal}
                    />
                )}
            >
                <RightAlignCell className="cell-innerText">
                    {value === -1 || value == null || value === 0
                        ? "-"
                        : noDataFilter()(numberFilter()(value, decimal), "^$", "-")}
                </RightAlignCell>
            </PopupHoverContainerStyled>
        );
    } else {
        return (
            <RightAlignCell className="cell-innerText">
                {value === -1 || value == null || value === 0
                    ? "-"
                    : noDataFilter()(numberFilter()(value, decimal), "^$", "-")}
            </RightAlignCell>
        );
    }
};
KeywordAnalysisPosition.displayName = "KeywordAnalysisPosition";
